<script setup>
import { usePageHead } from '@/composables/usePageHead'
import { withTrailingSlash } from '@/utils/siteUrl'
import home from '@/content/home.yml'
import NewsCard from '@/components/NewsCard.vue'
import ResourceCard from '@/components/ResourceCard.vue'
import ImpactCard from '@/components/ImpactCard.vue'
import AcademyCard from '@/components/AcademyCard.vue'
import StayInformed from '@/components/StayInformed.vue'
import Logos from '@/components/Logos.vue'
import SplitHeaderBlock from '@/components/SplitHeaderBlock.vue'
import Icon from '@/components/Icon.vue'
import IllustrationSplash from '@/components/IllustrationSplash.vue'

usePageHead(home)

const newsModules = import.meta.glob('/src/content/news/*.md', { eager: true })
const latestNews = Object.entries(newsModules)
  .map(([path, mod]) => ({
    slug: path.replace('/src/content/news/', '').replace('.md', ''),
    title: mod.title,
    date: mod.date,
    excerpt: mod.excerpt,
    author: mod.author,
    hide: mod.hide || [],
  }))
  .filter(a => !a.hide.includes('home'))
  .sort((a, b) => new Date(b.date) - new Date(a.date))
  .slice(0, 2)

const resourceModules = import.meta.glob('/src/content/resources/*.md', { eager: true })
const resourceMap = Object.fromEntries(
  Object.entries(resourceModules).map(([path, mod]) => [
    path.replace('/src/content/resources/', '').replace('.md', ''),
    mod,
  ])
)
const featuredResources = (home.featuredResources?.items || [])
  .map(item => {
    // An item is either a bare slug string, or an object { slug, thumbnail }
    // whose `thumbnail` overrides the resource's own logo on the homepage card
    // only — the /resources listing always uses the resource's .md thumbnail.
    const slug = typeof item === 'string' ? item : item.slug
    const thumbnailOverride = typeof item === 'object' ? item.thumbnail : ''
    const mod = resourceMap[slug]
    if (!mod) return null
    return {
      slug,
      title: mod.title,
      description: mod.description,
      type: mod.type,
      topics: mod.topics || [],
      publishedOn: mod.publishedOn || mod.publishedAt || '',
      externalUrl: mod.externalUrl || '',
      goInternal: mod.goInternal || false,
      thumbnail: thumbnailOverride || mod.thumbnail || '',
      thumbnailContain: mod.thumbnailContain || false,
    }
  })
  .filter(Boolean)

const ourWorkModules = import.meta.glob('/src/content/our-work/*.md', { eager: true })
const ourWorkMap = Object.fromEntries(
  Object.entries(ourWorkModules).map(([path, mod]) => [
    path.replace('/src/content/our-work/', '').replace('.md', ''),
    mod,
  ])
)
// Cards reference a real /our-work project by slug; body/cta default to that
// project's own excerpt + detail page so the homepage teaser can't drift out
// of sync with the actual story. `logo`/`body`/`cta` in home.yml still override
// per-card if set (used by the `graphic` display cards, which have no photo).
const impactCards = (home.impact.cards || []).map(card => {
  const project = card.ourWork ? ourWorkMap[card.ourWork] : null
  return {
    colour: card.colour,
    display: card.display || 'graphic',
    logo: card.logo || null,
    image: project ? { src: project.image, alt: project.title } : null,
    splash: card.splash || '',
    title: project?.snappyTitle || card.snappyTitle || '',
    body: card.body || project?.excerpt || '',
    cta: {
      label: card.cta?.label || 'Read more',
      to: withTrailingSlash(card.cta?.to || (card.ourWork ? `/our-work/${card.ourWork}` : '/our-work')),
    },
  }
})
</script>

<template>
  <div class="home">
    <section class="hero">
      <div v-if="home.hero.image" class="media">
        <picture>
          <!-- 767 = $bp-md (768px) - 1: plain HTML attribute, can't reach the
               SCSS variable directly, so kept in sync by hand. Tied to
               $bp-md (jk-37 — was $bp-hero-photo/568px, deliberately
               narrower "for genuinely small phones only"; the mismatch
               between that and the hero's own single→multi-column switch
               at $bp-md meant 568–767px got the single-column layout paired
               with the *desktop* crop) so the mobile crop now covers the
               entire single-column phase, matching the layout switch
               exactly. Sources ordered most-specific first: mobile+webp,
               mobile+png, then desktop/tablet+webp — the <img> below is the
               universal PNG fallback if a browser matches none (no webp
               support). -->
          <source v-if="home.hero.image.mobileWebp" :srcset="home.hero.image.mobileWebp" type="image/webp"
            media="(max-width: 767px)" />
          <source v-if="home.hero.image.mobileSrc" :srcset="home.hero.image.mobileSrc" media="(max-width: 767px)" />
          <source v-if="home.hero.image.webp" :srcset="home.hero.image.webp" type="image/webp" />
          <img class="photo" :src="home.hero.image.src" :alt="home.hero.image.alt" fetchpriority="high" width="918"
            height="768" />
        </picture>
      </div>
      <div class="inner">
        <div class="copy">
          <h1>{{ home.hero.heading }}</h1>
          <p class="sub">{{ home.hero.subheading }}</p>
          <RouterLink :to="withTrailingSlash(home.hero.cta.to)" class="btn primary pill hero-cta">
            {{ home.hero.cta.label }} <span aria-hidden="true">→</span>
          </RouterLink>
        </div>
      </div>
    </section>

    <section class="what-we-do">
      <div class="inner">
        <SplitHeaderBlock :heading="home.whatWeDo.heading">
          <div class="list">
            <div v-for="(item, i) in home.whatWeDo.items" :key="i" class="item">
              <Icon v-if="item.icon" :name="item.icon" size="xl" class="icon" />
              <p>{{ item.body }}</p>
            </div>
            <RouterLink :to="withTrailingSlash(home.whatWeDo.cta.to)" class="btn primary pill">
              {{ home.whatWeDo.cta.label }} <span aria-hidden="true">→</span>
            </RouterLink>
          </div>
        </SplitHeaderBlock>
      </div>
    </section>

    <section class="partner-band">
      <div class="inner">
        <div class="banner">
          <IllustrationSplash family="featureTonal" variation="B" colour="orange" contentSide="right"
            class="banner-illo" />
          <p class="eyebrow">{{ home.partners.eyebrow }}</p>
          <!-- Not a heading: this banner is a standalone statement, not a
               section with content beneath it — every sibling section on
               this page (h2) genuinely introduces one, this doesn't. Styled
               to match its former h2 treatment regardless. -->
          <p class="heading">{{ home.partners.heading }}</p>
        </div>
      </div>
    </section>

    <section class="impact">
      <div class="inner">
        <header class="head">
          <h2>{{ home.impact.heading }}</h2>
          <p class="sub">{{ home.impact.subheading }}</p>
        </header>
        <div class="cards">
          <ImpactCard v-for="(card, i) in impactCards" :key="i" :colour="card.colour" :display="card.display"
            :logo="card.logo" :image="card.image" :splash="card.splash" :title="card.title" :body="card.body"
            :cta="card.cta" />
        </div>
        <RouterLink :to="withTrailingSlash(home.impact.cta.to)" class="btn primary pill">
          {{ home.impact.cta.label }} <span aria-hidden="true">→</span>
        </RouterLink>
      </div>
    </section>

    <section class="academy">
      <div class="inner">
        <SplitHeaderBlock :heading="home.academy.heading">
          <p class="intro">{{ home.academy.intro }}</p>
        </SplitHeaderBlock>
        <div class="cards">
          <AcademyCard v-for="(card, i) in home.academy.cards" :key="i" :name="card.name" :quote="card.quote"
            :image="card.image" :colour="card.colour" :image-position="card.imagePosition" :cta="card.cta"
            :link="false" />
        </div>
        <RouterLink :to="withTrailingSlash(home.academy.cta.to)" class="btn primary pill">
          {{ home.academy.cta.label }} <span aria-hidden="true">→</span>
        </RouterLink>
      </div>
    </section>

    <section v-if="latestNews.length" class="news-section">
      <div class="container">
        <header class="head">
          <h2>{{ home.news.heading }}</h2>
          <p class="sub">{{ home.news.intro }}</p>
        </header>
        <div class="news-grid">
          <NewsCard v-for="article in latestNews" :key="article.slug" :slug="article.slug" :title="article.title"
            :date="article.date" :excerpt="article.excerpt" :author="article.author || ''" />
        </div>
        <div class="section-cta">
          <RouterLink :to="withTrailingSlash(home.news.cta.to)" class="btn primary">
            {{ home.news.cta.label }} <span aria-hidden="true">→</span>
          </RouterLink>
        </div>
      </div>
    </section>

    <section v-if="featuredResources.length" class="resources-section">
      <div class="container">
        <div class="header">
          <h2>{{ home.featuredResources.heading }}</h2>
          <p class="sub">{{ home.featuredResources.subheading }}</p>
        </div>
        <div class="resources-grid">
          <ResourceCard v-for="(resource, index) in featuredResources" :key="resource.slug" :slug="resource.slug"
            :title="resource.title" :description="resource.description" :type="resource.type" :topics="resource.topics"
            :publishedOn="resource.publishedOn" :externalUrl="resource.externalUrl" :goInternal="resource.goInternal"
            :thumbnail="resource.thumbnail" :thumbnailContain="resource.thumbnailContain" :colorIndex="index" />
        </div>
        <div class="section-cta">
          <RouterLink :to="withTrailingSlash(home.featuredResources.cta.to)" class="btn primary">
            {{ home.featuredResources.cta.label }} <span aria-hidden="true">→</span>
          </RouterLink>
        </div>
      </div>
    </section>

    <Logos class="logos-section" :heading="home.logos.heading" :intro="home.logos.intro" :subtitle="home.logos.subtitle"
      :items="home.logos.items" :ctaLabel="home.logos.cta.label" :ctaTo="home.logos.cta.to" />

    <StayInformed class="stay-informed-section" inline :heading="home.stayInformed.heading"
      :bullets="home.stayInformed.bullets" :formId="home.stayInformed.formId"
      :formBaseUrl="home.stayInformed.formBaseUrl" />
  </div>
</template>

<style lang="scss" scoped>
// No top padding: the full-bleed hero sits flush under the header. Keep a
// little breathing room below the last section before the footer.
.home {
  padding-block: 0 $space-8;
}

// ============================================================================
// HERO (Figma node 5:176) — jk-31, rewritten onto CSS grid 2026-08-08,
// reorganised by breakpoint 2026-08-08 (follow-up) for easier tuning.
//
// Bug fixed in this pass: every rule below is now written as `.hero .foo`
// (or nested inside `.hero { }`) instead of bare `.foo` — `.inner`/`.sub`
// are class names every other Home section also uses (What We Do, Partnering
// With, Impact, Academy), and a previous pass wrote `.inner` unscoped,
// silently applying `display: contents` to *all* of them from $bp-md up.
// `.hero-cta` (not `.cta`) for the same reason — `.cta` is a *global* class
// in `_components.scss` (the card "Read more →" underline-reveal style,
// unrelated), which collided and partially overrode the button's padding.
//
// Structure — four self-contained "views," one per breakpoint. To change
// how the hero looks at a given width, find that view below; each one
// carries everything that changes at that tier, so nothing relevant is
// hiding in a different section.
//   BASE            shared rules that hold across every tier
//   MOBILE VIEW     < $bp-md (768px)
//   TABLET VIEW     $bp-md – $bp-lg (768–1023px)
//   DESKTOP NARROW  $bp-lg – $bp-hero-heading-wide (1024–1299px)
//   DESKTOP WIDE    $bp-hero-heading-wide+ (1300px+)
//
// No object-fit/min-height cropping trick anywhere: `.photo` is always
// `width: 100%; height: auto` (see BASE), so the image renders at its own
// aspect ratio — full photo, every tier, by construction, not by
// cover/contain tuning.
// ============================================================================

// ── BASE — shared across every tier ─────────────────────────────────────────
.hero {
  background: var(--color-bg);
  max-width: $container;
  margin-inline: auto;
}

.hero .inner {
  padding-inline: $gutter;
}

.hero .copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.hero .copy h1 {
  font-weight: 700;
  line-height: $lh-heading;
  letter-spacing: $ls-heading;
  margin: 0;
  text-wrap: balance;
}

.hero .sub {
  color: var(--color-text);
  margin: 0;
  text-wrap: balance;
}

.hero .media {
  picture {
    display: block;
  }

  .photo {
    display: block;
    width: 100%;
    height: auto; // natural aspect ratio, sized only by whatever column it lands in — never cropped
  }
}

// ── MOBILE VIEW — below $bp-md (768px) ──────────────────────────────────────
// Stacked flow: heading, paragraph, CTA, then the photo full-bleed beneath.
.hero {
  display: flex;
  flex-direction: column;
  gap: $space-8;
  // No bottom padding — the photo (below the copy at this tier) should butt
  // straight up against "What we do" with no gap (jk-34), matching the
  // zero-gap convention the "Partnering with" banner uses lower on the page.
  padding: $space-10 0 0;

  .copy {
    gap: $space-6;
    max-width: 620px;
    position: relative;
    z-index: 2; // above the photo, which is full-bleed and sits at z-index:0`
  h1 {
    font-size: $font-size-h1;
  }
  .sub {
    font-size: $font-size-lg;
  }
  }

  // 48px

  .media {
    order: 2;
    position: relative;
    z-index:1;
    margin-top:-124px;
  }

  // photo after the copy, full-bleed edge to edge
}

// ── TABLET VIEW — $bp-md to $bp-lg (768–1023px) ─────────────────────────────
// Heading sits beside the photo in a 2-column grid; paragraph + CTA drop to
// full-width rows underneath. `.inner`/`.copy` both go `display: contents`
// so their children (heading, paragraph, CTA) land directly as `.hero`'s own
// grid items — that's also why the CTA needs its own `justify-self` below
// (a lone grid item defaults to `justify-self: stretch`).
@include from-md {
  .hero {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr; // best guess, even split — retune here
    grid-template-areas:
      "heading heading heading media media media"
      "cta   cta cta     media media media"
      "sub     sub     sub     sub sub     sub"
      ;
    column-gap: $space-8;
    row-gap: $space-4;
    align-items: start; // default for every grid item — .media relies on this; h1 overrides it below
    padding-block: $space-8;
    padding-inline: $gutter;
  }

  .hero .inner {
    display: contents;
  }

  .hero .copy {
    display: contents;
    font-size:1.5rem
  }

  .hero .copy h1 {
    font-size:clamp(2rem, 5vw, 4rem); // 32–48px — retune here if the heading is too small/large at either end
    grid-area: heading;
    align-self: center; // centres h1 vertically within row 1's height, which is set by the (likely taller) photo beside it
  }

  .hero .sub {
    grid-area: sub;
  }

  .hero .hero-cta {
    grid-area: cta;
    justify-self: start; // without this the button stretches full-width and loses its pill shape
  }

  .hero .media {
    order: initial;
    grid-area: media;
    align-self: start;
    margin-top:unset;
  }
}

// ── DESKTOP NARROW VIEW — $bp-lg to $bp-hero-heading-wide (1024–1299px) ─────
// Photo locks to a fixed column; `.copy` reunites into one real flex block
// beside it ("bring back flex display") instead of three separate grid
// cells — this is what stops the CTA stretching here too (flex children
// don't cross-axis-stretch by default, unlike grid items) and is what makes
// vertically centring the whole copy block possible via one `align-self`.
// Heading stays at a reduced size in this narrow band specifically: the text
// column is genuinely squeezed here (~200–480px), not enough room yet for
// the full display size.
@include from-md-lg {
  .hero {
    grid-template-columns: 1fr 390px; // fixed photo width — change here
    grid-template-areas: "copy media";
    grid-column-gap: 1rem;
  }

  .hero .copy {
    display: flex;
    flex-direction: column;
    max-width: none; // filling the grid's `1fr` text column now, not capped
    grid-area: copy;
    align-self: center; // vertically centre the copy block against the (likely taller) photo
    row-gap: 2rem;
  }

  .hero .copy h1 {
    font-size: 2rem
  }

  // 36px — knocked down again; change here if still too big/small

  .hero .sub {
    font-size: 1.125rem;
  }

  // 22px
}

@include from-lg {

  .hero {
    grid-template-columns: 1fr 490px; // fixed photo width — change here
    grid-template-areas: "copy media";
    grid-column-gap: 0px;

  .copy {
    row-gap: 2.5rem;

    h1 {
      font-size: 3rem; // 72px — change here
    }

    .sub {
      font-size: 1.25rem; // 28px — change here
    }
  }
}
}
// ── DESKTOP WIDE VIEW — $bp-hero-heading-wide+ (1300px+) ────────────────────
// Text column has grown enough (~480px+) to carry the full display heading.
@include from-hero-heading-wide {
  .hero {
    grid-template-columns: 1fr 590px; // fixed photo width — change here
    grid-template-areas: "copy media";

    .copy {
      row-gap: 2.5rem;

      h1 {
        font-size: 4rem; // 72px — change here
        line-height: 0.98;
        letter-spacing: 0;
      }

      .sub {
        font-size: 1.5rem; // 28px — change here
      }
    }
  }

}

// "Partnering with" banner — full-bleed cream band (= page bg) holding an
// orange rounded card. Figma node 75-2352; decorative squiggles intentionally
// omitted, so the copy is left-aligned across the full card.
.partner-band {
  background: var(--color-bg);
  // Lead-in banner on the same cream as .impact below it — no bottom padding,
  // so the two cream bands don't stack into a doubled vertical void. Impact's
  // own top padding provides the separation after the orange card.
  padding-block: $section-space-block 0;

  @include from-md {
    padding-block: $section-space-block-md 0;
  }

  .inner {
    max-width: $container;
    margin-inline: auto;
    padding-inline: $gutter;
  }

  .banner {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    background: var(--color-partner-band);
    border: 1px solid var(--color-partner-band-border);
    border-radius: $radius-lg;
    // Dedicated "on this band" token, not --color-bg (jk-9-dark-mode.md fix
    // wave; Known Issues #1 — --color-bg is the *page* background, reused
    // here as a foreground by coincidence-of-light-mode-values).
    color: var(--color-on-partner-band); // cream text on orange, both themes
    padding: $space-6;
    display: flex;
    flex-direction: column;
    gap: $space-5;

    // Feature tonal illustration (jk-21) — corner-accent tile, left side,
    // same mechanism as ContentHero.vue's full-height tile. `colour="orange"`
    // matches --color-partner-band exactly ($_orange-bright, aliased
    // separately as $color-card-orange) so the tile's own background reads
    // as one continuous fill with the card behind it, not a visible patch.
    // `variation="B"` is a fixed pick (not random) — this is one specific,
    // always-orange card, not a per-page hero, so there's no seeding/build
    // concern to solve here. `contentSide="right"` needs no flip — B's
    // native safe (line-free) edge is already its own right side, which
    // faces inward toward the copy now that the tile sits at the left.
    .banner-illo {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 35%;
      aspect-ratio: auto;
      border-radius: 0;
      z-index: 0;
      pointer-events: none;
    }

    // Copy shifts clear of the now-left-side tile — matches its width so
    // text starts right where the illustration ends, instead of overlapping it.
    .eyebrow,
    .heading {
      position: relative;
      z-index: 1;
      padding-left: 35%;
    }

    .eyebrow {
      margin: 0;
      font-size: $font-size-h4;
      // Bold qualifies this as WCAG "large text" (bold >= 18.66px), needing only a
      // 3:1 contrast ratio against $color-partner-band — cream-on-orange measures
      // ~3.08:1, which fails the 4.5:1 normal-text threshold this weight was at.
      font-weight: 700;
      line-height: $lh-heading-sm;
    }

    // Styled like an h2 (matches every sibling section's heading treatment)
    // without being one — see the template comment for why.
    .heading {
      margin: 0;
      font-size: $font-size-h2; // mobile-first; scales to heading-l at from-md
      font-weight: 500;
      line-height: $lh-heading;
      letter-spacing: $ls-heading;
      // No max-width — this band's text should use the full width available
      // beside the illustration, unlike a normal capped heading line-length.
    }

    @include from-md {
      padding: $space-8 $space-12; // 64px / 48px — generous banner band

      .heading {
        font-size: $font-size-h1;
        font-weight: 700;
      }

      // 48px — Figma heading-l, Bold per Styleguide p.8
    }

    // 900–1200px (jk-34): the from-md 48px heading crowded the copy column
    // against the illustration at this in-between width — sized down to
    // 2rem for this range only; narrower (illustration removed, below) and
    // wider (full band) keep their existing sizes.
    @media (min-width: 900px) and (max-width: 1199.98px) {
      .heading {
        font-size: 2rem;
      }
    }

    // Below 900px (jk-34): drop the corner illustration entirely — at
    // mobile/small-tablet widths it left too little room for the copy — and
    // reclaim the 35% left inset that existed only to clear it.
    @media (max-width: 899.98px) {
      .banner-illo {
        display: none;
      }

      .eyebrow,
      .heading {
        padding-left: 0;
      }
    }
  }
}

// Our impact — full-bleed cream band after the hero (Figma node 75-2360).
.impact {
  background: var(--color-bg);
  // Tighter bottom than top: trims the cream gap before the purple Academy
  // band (the "space after Our Impact").
  padding-block: $section-space-block;

  @include from-md {
    padding-block: $section-space-block-md $section-space-block;
  }

  .inner {
    max-width: $container;
    margin-inline: auto;
    padding-inline: $gutter;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-6;
  }

  .head {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: $space-5;

    h2 {
      margin: 0;
      color: var(--color-ink);
    }

    .sub {
      margin: 0;
      font-size: $font-size-h4;
      line-height: $lh-heading-sm;
      color: var(--color-ink);
    }
  }

  .cards {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-5;

    // Three cards now (was 2) — matches the /our-work listing grid exactly:
    // 2-up at md, 3-up at lg, same card size as there.
    @include from-md {
      grid-template-columns: repeat(2, 1fr);
    }

    @include from-lg {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

// The Academy — full-bleed solid purple band after "Our impact" (Figma node
// 75-2435). Header is title + intro (cream); two whole testimonial cards sit
// side-by-side at from-md (no carousel peek); centred blue pill CTA below.
.academy {
  background: var(--color-academy-band);
  padding-block: $section-space-block;

  @include from-md {
    padding-block: $section-space-block-md;
  }

  .inner {
    max-width: $container;
    margin-inline: auto;
    padding-inline: $gutter;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-6;
  }

  // SplitHeaderBlock's own scoped h2/p are styled for the cream page (dark
  // ink), so its text needs the cream-on-purple override here (:deep, since
  // that markup is rendered by the child, not slotted). The intro paragraph
  // below is our own slotted content — its own `.intro` class works
  // directly, no :deep() needed.
  :deep(.split-header-block) {
    width: 100%;

    .header h2 {
      color: var(--color-on-banner); // cream on purple
    }
  }

  .intro {
    margin: 0;
    font-size: $font-size-h4; // 22px — Figma heading-s
    line-height: $lh-heading-sm;
    color: var(--color-on-banner); // cream on purple
    text-wrap: pretty;
  }

  .cards {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-8;

    // jk-37: was from-md (768px) — each AcademyCard is already its own
    // 2-column image+text layout (that one's untouched, still switches at
    // its own 650px), so two side-by-side at 768px meant 4 columns' worth
    // of content crammed into a tablet-width row. Stacked (1-up) until the
    // new from-md-lg (920px), matching side-by-side only once there's
    // actually room for it.
    @include from-md-lg {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

// What we do — full-bleed cream band directly below the hero (Figma node
// 26-2356). Heading sits in a left column; a divided list of service
// statements + a "learn more" CTA fills the right column at from-md — that
// split is SplitHeaderBlock's own grid, so .inner stays a plain container.
.what-we-do {
  background: var(--color-bg-alt); // darker cream band (Figma aut-cream2) — alternates with the cream sections
  // Local override, not the shared $section-space-block(-md) — this band
  // reads as too tall at the site's standard rhythm; other sections keep it.
  padding-block: $space-5;

  @include from-md {
    padding-block: $space-12;
  }

  .inner {
    max-width: $container;
    margin-inline: auto;
    padding-inline: $gutter;
  }

  .head h2 {
    margin: 0;
    color: var(--color-ink);
    font-size: $font-size-h2;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: $space-5;
    align-items: flex-start;
  }

  .item {
    width: 100%;
    display: flex;
    gap: $space-5;
    align-items: center;

    .icon {
      flex: none;
      // Icon's own default colour is now var(--color-ink) (jk-31 follow-up)
      // — no override needed here, this already matches the statement text.
    }

    p {
      margin: 0;
      font-size: $font-size-lg;
      line-height: $lh-base;
      color: var(--color-ink);
    }
  }
}

// Shared bottom-of-section CTA (used by .news-section and .resources-section):
// centered, spaced off the grid, with a pill-radius button.
.section-cta {
  margin-top: $space-8;
  text-align: center;

  .btn {
    border-radius: $radius-pill;
  }
}

.news-section {
  padding-block: $section-space-block;

  @include from-md {
    padding-block: $section-space-block-md;
  }

  .head {
    margin-bottom: $space-8;

    h2 {
      margin: 0 0 $space-2;
    }

    .sub {
      margin: 0;
      font-size: $font-size-h4;
      line-height: $lh-heading-sm;
      color: var(--color-ink);
    }
  }

  .news-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-6;

    @include from-sm {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

// Full-bleed warm band (Figma node 36-3991). Sits outside the page .container
// as a direct child of .home (same technique as .hero); an inner .container
// constrains its content. (No negative bottom margin: the Logos + StayInformed
// sections now follow this band, so pulling them up would overlap this band's
// CTA onto the next section's top edge.)
.resources-section {
  background: var(--color-section-alt);
  padding-block: $section-space-block;

  @include from-md {
    padding-block: $section-space-block-md;
  }

  .header {
    margin-bottom: $space-8;

    h2 {
      margin: 0 0 $space-2;
    }

    .sub {
      margin: 0;
      max-width: 640px;
      font-size: $font-size-lg;
      color: var(--color-text);
    }
  }

  .resources-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-6;

    @include from-sm {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}
</style>
